import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Label } from "./label";

export interface CampoProps {
  label: ReactNode;
  requerido?: boolean;
  /** Texto auxiliar debajo del control. */
  ayuda?: ReactNode;
  /** Mensaje de error debajo del control; ademas marca el control como invalido. */
  error?: ReactNode;
  /** El control (`Input`, `Textarea`, `SelectNativo`, `InputBusqueda`...). */
  children: ReactNode;
  className?: string;
}

/** Lo que Campo le inyecta al control por `cloneElement`. */
interface PropsControl {
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false" | "grammar" | "spelling";
}

/**
 * Par label + control con la asociacion hecha de verdad: el control recibe un
 * `id` (si no trae uno) y la label lo apunta con `htmlFor`; ayuda y error
 * quedan enlazados por `aria-describedby`. Sin esto, ni el lector de pantalla
 * ni `getByLabelText` encuentran el campo. Reemplaza a los `Campo` locales de
 * DialogUsado, DialogAlta y catalogo/Campos.
 *
 * El error lleva `role="alert"` para que se anuncie al aparecer, y el control
 * pasa a `aria-invalid` (que `claseCampo` pinta de rojo).
 */
export function Campo({
  label,
  requerido = false,
  ayuda,
  error,
  children,
  className,
}: CampoProps): JSX.Element {
  const idBase = useId();
  const hijo = isValidElement<PropsControl>(children) ? children : null;
  const id = hijo?.props.id ?? `${idBase}-control`;
  const idAyuda = ayuda ? `${idBase}-ayuda` : undefined;
  const idError = error ? `${idBase}-error` : undefined;

  const describedBy =
    [hijo?.props["aria-describedby"], idError, idAyuda].filter(Boolean).join(" ") || undefined;

  const control: ReactNode = hijo
    ? cloneElement<PropsControl>(hijo as ReactElement<PropsControl>, {
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : hijo.props["aria-invalid"],
      })
    : children;

  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={id} requerido={requerido}>
        {label}
      </Label>
      {control}
      {ayuda ? (
        <p id={idAyuda} className="text-xs text-muted-foreground">
          {ayuda}
        </p>
      ) : null}
      {error ? (
        <p id={idError} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface GrupoCampoProps {
  label: ReactNode;
  requerido?: boolean;
  ayuda?: ReactNode;
  error?: ReactNode;
  /** El grupo (`Segmented`, tarjetas-radio, casillas). */
  children: ReactNode;
  className?: string;
}

/**
 * Lo mismo que `Campo`, pero para un GRUPO de botones (memoria, rubro, caja y
 * cable) en vez de un control.
 *
 * No se puede usar `Campo`: ese asocia la label clonando al hijo con un `id`, y
 * un grupo no es un control —el nombre accesible lo lleva el propio grupo, con
 * su `etiqueta`. La label de acá es visual y ordena la columna; el error va en
 * el mismo lugar y con la misma pinta que el de cualquier campo, que es la
 * razon de que esto exista en `ui/` y no copiado en cada diálogo.
 */
export function GrupoCampo({
  label,
  requerido = false,
  ayuda,
  error,
  children,
  className,
}: GrupoCampoProps): JSX.Element {
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label requerido={requerido}>{label}</Label>
      {children}
      {ayuda ? <p className="text-xs text-muted-foreground">{ayuda}</p> : null}
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
